// src/pages/SitePage.jsx
//
// SiteAgent — what is on this pad. The second FieldAssist product.
//
// Same shape and the same constraints as RoutePage: built for 390x844 first,
// full viewport, shell-less off fieldRoute, high contrast for sunlight, every
// target at least 44pt, nothing revealed by hover. 768x1024 widens the sheet
// and nothing else.
//
// ── THE ONE RULE THAT DRIVES THE LIST ─────────────────────────────────────
//
// A category this organisation does not hold renders as ITSELF, named, in a
// distinct state — not as an empty list, and not omitted. Those three
// renderings look similar and mean completely different things:
//
//   omitted      the rep concludes the ground has no tanks
//   empty list   the rep concludes the ground has no tanks
//   "not licensed — Production Equipment"
//                the rep knows there may be tanks and that their
//                organisation has not bought the layer, which is
//                something they can actually act on
//
// Earl O&G makes this the common case rather than an edge one: one grant,
// `wellsite`, so four of the five grantable categories are unlicensed on every
// pad they open. On the pad used to verify this, DreamHammer sees 78 assets
// across 20 classes and Earl sees 23 — and without this rule the other 55
// would simply look like bare ground.
//
// The API supports this directly: every category comes back, with `granted`
// and with `asset_count: null` rather than 0 for the ungranted ones. Null is
// the honest value — the server read through vector_assets_visible, so it
// never counted them either.

import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../lib/map/whenMapLoaded";
import { ArrowLeft, Loader2, AlertTriangle, Lock, MapPin, Camera } from "lucide-react";
import { useAppNav } from "../hooks/useAppNav";
import { useAuthStore } from "../stores/useAuthStore";
import { useSiteAgentStore } from "../stores/useSiteAgentStore";
import { buildTransformRequest } from "../api/mapTransformRequest";
import { basemapTileUrl, useBasemapStore } from "../stores/useBasemapStore";
import { attachAttribution, basemapAttribution } from "../lib/map/attribution";
import { useFieldImagery } from "../hooks/useFieldImagery";
import FieldImageryToggle from "../components/field/FieldImageryToggle";

const PAD_TILES = `${window.location.origin}/vtiles/well_pad/{z}/{x}/{y}.pbf`;

const SRC_PADS = "site-pads";
const SRC_SEL = "site-selected-pad";
const SRC_ASSETS = "site-assets";
const L_PADS_FILL = "site-pads-fill";
const L_PADS_LINE = "site-pads-line";
const L_SEL_LINE = "site-selected-line";
const L_ASSET_PT = "site-assets-point";
const L_ASSET_LINE = "site-assets-line";
const L_ASSET_FILL = "site-assets-fill";

const EMPTY_FC = { type: "FeatureCollection", features: [] };

// Same three stops as RoutePage. A continuous sheet on a phone in a truck ends
// up somewhere useless.
const STOPS = [0.30, 0.58, 0.90];

// Category colours. Deliberately few and strongly separated — this is read in
// sunlight, and a twenty-class palette is unreadable at any brightness.
const CAT_COLOR = {
  wellsite: "#22d3ee",
  production_equipment: "#f59e0b",
  linear_infrastructure: "#a78bfa",
  environmental: "#34d399",
  utility: "#f472b6",
  regulatory: "#94a3b8",
  land: "#94a3b8",
  uncategorised: "#64748b",
};
const colorFor = (k) => CAT_COLOR[k] ?? "#64748b";

function formatArea(m2) {
  if (m2 == null) return null;
  if (m2 >= 10000) return `${(m2 / 10000).toFixed(1)} ha`;
  return `${Math.round(m2)} m²`;
}

// The imagery date, shown ONCE. Not per asset: every observation on a pad comes
// off the same granule, so repeating it against twenty rows is twenty copies of
// one fact. The endpoint returns an array precisely so that the rare pad whose
// assets span two acquisitions can say so instead of being rendered as one.
function ImageryDateLine({ dates }) {
  if (!dates?.length) return null;
  const fmt = (d) => new Date(d).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
  return (
    <div className="flex items-center gap-2 text-[15px] font-bold text-slate-300">
      <Camera size={17} className="shrink-0" />
      {dates.length === 1
        ? <span>Seen on imagery {fmt(dates[0])}</span>
        : <span>Imagery {dates.map(fmt).join(" and ")} — this pad spans two dates</span>}
    </div>
  );
}

export default function SitePage() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const didInitRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [stop, setStop] = useState(1);
  const dragRef = useRef(null);

  const basemapId = useBasemapStore((s) => s.activeBasemapId);
  const nav = useAppNav();

  const pad = useSiteAgentStore((s) => s.pad);
  const categories = useSiteAgentStore((s) => s.categories);
  const totals = useSiteAgentStore((s) => s.totals);
  const imageryDates = useSiteAgentStore((s) => s.imageryDates);
  const radiusM = useSiteAgentStore((s) => s.radiusM);
  const loading = useSiteAgentStore((s) => s.loading);
  const error = useSiteAgentStore((s) => s.error);
  const selectedCategory = useSiteAgentStore((s) => s.selectedCategory);
  const selectedClassKey = useSiteAgentStore((s) => s.selectedClassKey);
  const loadPad = useSiteAgentStore((s) => s.loadPad);
  const selectGroup = useSiteAgentStore((s) => s.selectGroup);

  const imagery = useFieldImagery(mapRef, mapReady);

  // ── map ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (didInitRef.current || !containerRef.current) return;
    didInitRef.current = true;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          base: {
            type: "raster",
            tiles: [basemapTileUrl(basemapId)],
            tileSize: 256,
            attribution: basemapAttribution(basemapId),
          },
        },
        layers: [{ id: "base", type: "raster", source: "base" }],
      },
      center: [-101.42, 31.08],
      zoom: 12,
      attributionControl: { compact: false },
      maxzoom: 20,
      // The pad vector tiles are entitlement-gated like every other tile in
      // this app; without the bearer they 401 and the map renders nothing to
      // tap. Shared with every other map on purpose — a second copy of the
      // tile-auth contract is how the two drift.
      transformRequest: buildTransformRequest(() => useAuthStore.getState().token),
    });
    // Mapbox's logo and credit (REM-684).
    attachAttribution(map, { mapboxSource: "base" });
    mapRef.current = map;
    // The drive handle for this view, matching RoutePage's window.__routeMap.
    // Without it a headless verification run can only click blind pixels and
    // hope a pad is under them.
    window.__siteMap = map;

    whenMapLoaded(map, () => {
      map.addSource(SRC_PADS, { type: "vector", tiles: [PAD_TILES], minzoom: 8, maxzoom: 16 });
      // "assets", not "well_pad": the asset type selects the tile ROUTE, while
      // the MVT layer inside it is named by api.vector_tile's ST_AsMVT and is
      // the same for every asset type.
      map.addLayer({
        id: L_PADS_FILL, type: "fill", source: SRC_PADS, "source-layer": "assets",
        paint: { "fill-color": "#22d3ee", "fill-opacity": 0.18 },
      });
      map.addLayer({
        id: L_PADS_LINE, type: "line", source: SRC_PADS, "source-layer": "assets",
        paint: { "line-color": "#22d3ee", "line-width": 1.5 },
      });

      map.addSource(SRC_SEL, { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: L_SEL_LINE, type: "line", source: SRC_SEL,
        paint: { "line-color": "#ffffff", "line-width": 3 },
      });

      map.addSource(SRC_ASSETS, { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: L_ASSET_FILL, type: "fill", source: SRC_ASSETS,
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.28 },
      });
      map.addLayer({
        id: L_ASSET_LINE, type: "line", source: SRC_ASSETS,
        filter: ["!=", ["geometry-type"], "Point"],
        paint: { "line-color": ["get", "color"], "line-width": ["case", ["get", "hot"], 5, 2] },
      });
      map.addLayer({
        id: L_ASSET_PT, type: "circle", source: SRC_ASSETS,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-radius": ["case", ["get", "hot"], 11, 6],
          "circle-color": ["get", "color"],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#020617",
        },
      });

      setMapReady(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      didInitRef.current = false;
      delete window.__siteMap;
    };
    // Basemap id is read once at construction; changing it is not a flow this
    // view offers, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tap a pad to load it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const onClick = (e) => {
      const hits = map.queryRenderedFeatures(e.point, { layers: [L_PADS_FILL] });
      if (!hits.length) return;
      const id = hits[0].properties?.id ?? hits[0].id;
      if (typeof id === "string") loadPad(id);
    };
    map.on("click", onClick);
    return () => map.off("click", onClick);
  }, [mapReady, loadPad]);

  // Selected pad outline + frame.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!pad?.geometry) { map.getSource(SRC_SEL)?.setData(EMPTY_FC); return; }
    map.getSource(SRC_SEL)?.setData({
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: pad.geometry, properties: {} }],
    });
    if (pad.bbox) {
      map.fitBounds([[pad.bbox[0], pad.bbox[1]], [pad.bbox[2], pad.bbox[3]]], {
        padding: {
          top: 90, left: 40, right: 40,
          bottom: Math.round((containerRef.current?.clientHeight ?? window.innerHeight) * STOPS[stop]) + 30,
        },
        maxZoom: 18,
        duration: 500,
      });
    }
    // `stop` intentionally excluded: refitting on every sheet drag fights the
    // person's own panning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pad, mapReady]);

  // Assets, with the selected group highlighted. One source, a `hot` flag per
  // feature — a second source for the highlight would have to be kept in step
  // with the first, and that is a bug waiting rather than a saving.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const features = [];
    for (const c of categories) {
      for (const g of c.groups ?? []) {
        const hot = selectedCategory === c.key && selectedClassKey === g.class_key;
        for (const a of g.assets ?? []) {
          if (!a.geometry) continue;
          features.push({
            type: "Feature",
            geometry: a.geometry,
            properties: { id: a.id, category: c.key, class_key: a.class_key, color: colorFor(c.key), hot },
          });
        }
      }
    }
    map.getSource(SRC_ASSETS)?.setData({ type: "FeatureCollection", features });
  }, [categories, selectedCategory, selectedClassKey, mapReady]);

  // Sheet drag — pointer events, so touch and mouse are one path.
  const onDragStart = (e) => {
    dragRef.current = { y: e.clientY, stop };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e) => {
    if (!dragRef.current) return;
    const dy = dragRef.current.y - e.clientY;
    if (Math.abs(dy) < 40) return;
    const next = Math.max(0, Math.min(STOPS.length - 1, dragRef.current.stop + (dy > 0 ? 1 : -1)));
    setStop(next);
    dragRef.current = null;
  };
  const onDragEnd = () => { dragRef.current = null; };

  const tapGroup = useCallback((catKey, classKey) => {
    selectGroup(catKey, classKey);
  }, [selectGroup]);

  const granted = categories.filter((c) => c.granted);
  const withheld = categories.filter((c) => !c.granted);

  return (
    <div className="fixed inset-0 bg-slate-950 text-white select-none overflow-hidden">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-3 flex flex-wrap items-start gap-2 pointer-events-none">
        <button
          type="button"
          onClick={() => nav.goField()}
          aria-label="Back to FieldAssist"
          className="pointer-events-auto shrink-0 min-h-[48px] min-w-[48px] rounded-xl bg-slate-950/95 border-2 border-slate-700 flex items-center justify-center active:bg-slate-700"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="pointer-events-none rounded-xl bg-slate-950/95 border-2 border-slate-700 px-3 py-2">
          <div className="text-[11px] font-bold font-mono uppercase tracking-[0.18em] text-slate-400 leading-none">
            AI PRIME
          </div>
          <div className="text-[19px] font-black leading-tight">SiteAgent</div>
        </div>
        {/* ml-auto on a wrapping row rather than a flex-1 spacer — see the
            same note in RoutePage. At 390 the back button and the title block
            leave too little for the toggle, and without wrapping its date list
            and licence copy ran off the right edge of the screen. */}
        <div className="ml-auto">
          <FieldImageryToggle imagery={imagery} className="pointer-events-auto" />
        </div>
      </div>

      {/* Sheet */}
      <div
        className="absolute left-0 right-0 bottom-0 bg-slate-950 border-t-2 border-slate-700 rounded-t-3xl flex flex-col md:max-w-[520px] md:left-auto md:right-0 md:rounded-tr-none"
        style={{ height: `${STOPS[stop] * 100}%` }}
      >
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          role="separator"
          aria-label="Resize panel"
          className="shrink-0 h-9 flex items-center justify-center touch-none cursor-ns-resize"
        >
          <div className="w-14 h-1.5 rounded-full bg-slate-600" />
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-5">
          {!pad && !loading && !error && (
            <div className="pt-6 text-center">
              <MapPin size={38} className="mx-auto text-cyan-400" />
              <p className="mt-3 text-[19px] font-black">Tap a well pad</p>
              <p className="mt-1 text-[15px] font-semibold text-slate-400 leading-snug">
                SiteAgent lists what is on it and within {radiusM} m of it.
              </p>
            </div>
          )}

          {loading && (
            <div className="pt-8 flex items-center justify-center gap-3 text-[17px] font-bold text-slate-300">
              <Loader2 size={22} className="animate-spin" /> Loading this pad
            </div>
          )}

          {error && !loading && (
            <div className="pt-5 flex items-start gap-3">
              <AlertTriangle size={22} className="shrink-0 text-amber-300 mt-0.5" />
              <p className="text-[16px] font-semibold text-amber-300 leading-snug">{error}</p>
            </div>
          )}

          {pad && !loading && !error && (
            <>
              <div className="pt-1 pb-3 border-b-2 border-slate-800">
                <div className="text-[26px] font-black leading-none">
                  {totals?.assets ?? 0} {totals?.assets === 1 ? "asset" : "assets"}
                </div>
                <div className="mt-1.5 text-[15px] font-semibold text-slate-300">
                  {totals?.inside ?? 0} on the pad · {totals?.within_50m ?? 0} within {radiusM} m
                  {pad.area_m2 != null && <> · pad {formatArea(pad.area_m2)}</>}
                </div>
                <div className="mt-2">
                  <ImageryDateLine dates={imageryDates} />
                </div>
              </div>

              {/* What is here */}
              <ul className="pt-3 space-y-3">
                {granted.map((c) => (
                  <li key={c.key}>
                    <div className="flex items-center gap-2">
                      <span
                        className="shrink-0 w-3.5 h-3.5 rounded-sm"
                        style={{ background: colorFor(c.key) }}
                        aria-hidden="true"
                      />
                      <h2 className="text-[17px] font-black">{c.label}</h2>
                      <span className="text-[15px] font-bold text-slate-400">{c.asset_count}</span>
                    </div>

                    {c.groups?.length ? (
                      <ul className="mt-1.5 space-y-1.5">
                        {c.groups.map((g) => {
                          const sel = selectedCategory === c.key && selectedClassKey === g.class_key;
                          return (
                            <li key={g.class_key}>
                              <button
                                type="button"
                                onClick={() => tapGroup(c.key, g.class_key)}
                                aria-pressed={sel}
                                className={`w-full min-h-[52px] px-4 rounded-xl border-2 flex items-center justify-between gap-3 text-left ${
                                  sel
                                    ? "bg-cyan-400 border-cyan-200 text-slate-950"
                                    : "bg-slate-900 border-slate-800 text-white active:bg-slate-800"
                                }`}
                              >
                                <span className="text-[17px] font-bold truncate">{g.label}</span>
                                <span className="text-[19px] font-black shrink-0">{g.count}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      // Licensed and genuinely empty. This is the state the
                      // withheld block below must never be confused with.
                      <p className="mt-1 px-1 text-[15px] font-semibold text-slate-500">
                        None on this pad.
                      </p>
                    )}
                  </li>
                ))}
              </ul>

              {/* NOT LICENSED — named, never omitted, never rendered as empty. */}
              {withheld.length > 0 && (
                <div className="mt-5 pt-4 border-t-2 border-slate-800">
                  <h2 className="text-[13px] font-bold font-mono uppercase tracking-[0.16em] text-slate-400">
                    Not licensed to this organization
                  </h2>
                  <ul className="mt-2 space-y-2">
                    {withheld.map((c) => (
                      <li
                        key={c.key}
                        className="min-h-[52px] px-4 py-2.5 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center gap-3"
                      >
                        <Lock size={19} className="shrink-0 text-amber-300" />
                        <span className="min-w-0">
                          <span className="block text-[17px] font-bold text-slate-300">{c.label}</span>
                          {/* NOT "0". The count is unknown here, deliberately —
                              the server read through the entitlement view, so it
                              did not count these either. */}
                          <span className="block text-[14px] font-semibold text-slate-500 leading-snug">
                            There may be assets here. Your organization is not
                            licensed for this layer.
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="mt-5 text-[13px] font-semibold text-slate-500 leading-snug">
                Advisory only. Extracted from imagery and not verified on the ground.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
